import { EventType, sendBeacon } from './beacon';
import { log } from './log';
import { getEntityFromURL } from '../utils/getEntityFromURL';
import { videoSegment } from './videoSegment';
import { fbiaEnrichTag } from './fbiaEnrichment';
import EventParams = jwplayer.EventParams;
import { metricsInit } from './metrics/loader';
import { VideoProvider } from '../constants/Video';

type PixelsSetupParam = {
  mediaId: string;
  playerId: string;
  jwplayerSiteId: string;
  pageOriginUrl: string;
  playlist: string;
  generateSEOMetadata: boolean;
  related: {
    file?: string;
    displayMode?: string;
  };
  skin: {
    name?: string;
  };
  setTimeEvents: boolean;
  isSwapped: boolean;
  platform: string;
};

let playerInited = false;
let currentPlayCount = 0;
let isFbia = false;

const loadPlayerScript = (playerId: string, callback: any) => {
  playerInited = true;
  const script = document.createElement('script');
  script.defer = true;
  script.onload = callback;
  script.src = `https://cdn.jwplayer.com/libraries/${playerId}.js`;
  document.head.appendChild(script);
};

const setupWithRecommendationsAndTracking = async (domId: string, mediaId: string, options: PixelsSetupParam) => {
  const { jwplayer } = window;
  const base = __RELATED_VIDEO_ENDPOINT__;
  const originUrl = options.pageOriginUrl || document.location.href;
  const { id: pageId, hostname } = getEntityFromURL(originUrl);
  const recommendationsPlaylist = jwplayer.defaults?.related?.file || jwplayer(domId).getConfig()?.related?.file;

  if (recommendationsPlaylist) {
    const playlistUrl = encodeURIComponent(recommendationsPlaylist.replace('MEDIAID', mediaId));
    options.related = {
      file: `${base}/?playListUrl=${playlistUrl}&pageOriginUrl=${originUrl}`,
    };
  }
  options.playlist = `https://cdn.jwplayer.com/v2/media/${mediaId}`;
  jwplayer(domId).setup(options);

  if (options.platform === 'fbia') {
    isFbia = true;
    // configure ready event handler, as this is missing on the indy side...
    jwplayer(domId).on('ready', () => {
      let fbiaAdTag = pixels(domId).getAdTag();
      // do light touch enrichment with the values we have (missing topics, grapeshot,
      // permutive, etc.)
      fbiaAdTag = fbiaEnrichTag(fbiaAdTag, mediaId, 'autoplay', options.pageOriginUrl);
      pixels(domId).setAdTag(fbiaAdTag);
    });
  }

  const trackVideoEvent = (type: EventType) => () => {
    const { mediaid: id } = jwplayer(domId).getPlaylistItem();
    sendBeacon({
      id,
      host: hostname,
      eventType: type,
      href: originUrl,
      platform: options.platform || 'native',
    });
  };

  const trackVideoEventOnce = (type: EventType) => {
    let played = false;
    return () => {
      if (played) return;
      const { mediaid: id } = jwplayer(domId).getPlaylistItem();
      sendBeacon({
        id: `${pageId}-${id}`,
        host: hostname,
        eventType: type,
        href: originUrl,
        platform: options.platform || 'native',
      });
      played = true;
    };
  };

  jwplayer(domId).on('firstFrame', trackVideoEvent(EventType.VIDEO_PLAY));
  jwplayer(domId).on('firstFrame', trackVideoEventOnce(EventType.FIRST_VIDEO_PLAY));
  jwplayer(domId).on('complete', trackVideoEvent(EventType.VIDEO_COMPLETED));

  if (options.isSwapped) {
    // run this only once
    jwplayer(domId).on('ready', trackVideoEvent(EventType.ADDED_BY_PIXELS));
  }

  // start pixels ad processing logic
  jwplayer(domId).on('playlistItem', () => {
    currentPlayCount++;
    log('pixels', 'playlistItem CALLED', currentPlayCount);
  });

  jwplayer(domId).on('beforeComplete', () => {
    log('pixels', 'beforeComplete CALLED', currentPlayCount);
    processNextVideoAdTag();
  });

  // somehow the nextClick is not on jwplayer event param
  // @ts-ignore
  jwplayer(domId).on('nextClick', () => {
    log('pixels', 'nextClick CALLED', currentPlayCount);
    processNextVideoAdTag();
  });

  const processNextVideoAdTag = () => {
    if (currentPlayCount > 0) {
      // run own ad tag setting for second video + ...
      let adTag;
      if (jwplayer(domId).getConfig().advertising?.schedule?.[0]?.tag) {
        adTag = jwplayer(domId).getConfig().advertising.schedule[0].tag;
      } else if (jwplayer.defaults?.advertising?.schedule?.[0]?.tag) {
        adTag = jwplayer.defaults.advertising.schedule[0].tag;
      }
      enrichAndSetAdTag(domId, adTag, true);
    }
  };
  // end pixels ad processing logic

  // load the video-metrics
  try {
    await metricsInit(domId, VideoProvider.JWPLAYER, pageId);
  } catch (e) {
    console.error(e);
  }
};

const createNewIu = (iu: string) => {
  const parts = iu.split('/');
  const publicationSlug = parts[1];
  const isEspanol = /_spanish/.test(publicationSlug);
  const isAmp = /_amp/.test(publicationSlug);
  return `22844376944/Independent_Preroll/Indy${isEspanol ? '_Espanol' : ''}${
    isFbia ? '_FBIA' : isAmp ? '_Amp' : '_Native'
  }`;
};

const enrichAndSetAdTag = (domId: string, adTag: string, setByPixels = false) => {
  const { jwplayer } = window;
  const mediaId = jwplayer(domId).getConfig().playlistItem.mediaid;
  log('pixels media id', mediaId);

  const parts = new URL(adTag);
  const base = parts.origin + parts.pathname;
  const urlParams = new URLSearchParams(parts.search);

  if (urlParams.has('cust_params') && cacheSegments[mediaId]) {
    const custParams = urlParams.get('cust_params');
    urlParams.set('cust_params', custParams + '&ad_intelligence=' + encodeURIComponent(cacheSegments[mediaId]));
  }
  if (setByPixels) {
    // alter iu
    if (urlParams.has('iu')) {
      urlParams.set('iu', createNewIu(urlParams.get('iu')));
    }
  }
  let tag = `${base}?${urlParams.toString()}`;
  if (setByPixels) {
    // update adpos
    tag = tag.replace(/(adpos%3D).*?(%26)/, '$1' + currentPlayCount + '$2');
    // update videoID
    tag = tag.replace(/(videoID%3D).*?(%26)/, '$1' + mediaId + '$2');
    log('pixels', 'setAdTag called BY PIXELS', tag);
  } else {
    log('pixels', 'setAdTag called', tag);
  }
  if (jwplayer(domId).getConfig().advertising?.schedule?.[0]?.tag) {
    jwplayer(domId).getConfig().advertising.schedule[0].tag = tag;
  } else if (jwplayer.defaults?.advertising?.schedule?.[0]?.tag) {
    jwplayer.defaults.advertising.schedule[0].tag = tag;
  }
};

const cacheSegments: Record<string, any> = {};

const addLogo = (el: any, logo: string) => {
  const img = document.createElement('img');
  img.src = logo;
  img.style.position = 'absolute';
  img.style.left = '50%';
  img.style.top = '50%';
  img.style.width = '10%';
  img.style.marginLeft = '-5%';
  img.style.marginTop = '-5%';
  el.appendChild(img);
};

export default function pixels(domId: string) {
  return {
    initPlayer: (playerId: string, callback: any, bgcolor: string, logo: string) => {
      log('pixels', 'wrapper-independent', 'initPlayer called with playerId', playerId, bgcolor, logo);
      loadPlayerScript(playerId, callback);
      const el = document.getElementById(domId);
      el.style.position = 'relative';
      el.style.width = '100%';
      el.style.paddingTop = '56.25%';
      if (bgcolor) {
        el.style.background = bgcolor;
      }
      if (logo) {
        addLogo(el, logo);
      }
    },
    setup: (options: PixelsSetupParam) => {
      const playerId = options.playerId;
      const mediaId = options.mediaId;
      // start request the segments
      videoSegment(options.jwplayerSiteId || 'g7SpqmnK', mediaId).then(result => {
        cacheSegments[mediaId] = result;
      });

      if (playerInited) {
        log('pixels', 'setup called with mediaId', mediaId);
        setupWithRecommendationsAndTracking(domId, mediaId, options);
      } else {
        log('pixels', 'setup called with mediaId and playerId', mediaId, playerId);
        loadPlayerScript(playerId, () => {
          setupWithRecommendationsAndTracking(domId, mediaId, options);
        });
      }
    },
    test: (params: any) => {
      log('pixels', 'test called', params);
    },
    initPlaylistPrebidCallback: (callback: (x: string) => Promise<string>) => {
      log('pixels', 'initPlaylistPrebidCallback', 'called');
    },
    getVideoPlayerInstance: () => {
      const { jwplayer } = window;
      return jwplayer;
    },
    on: (event: string, func: any) => {
      log('pixels', 'on inited for event', event);
      const { jwplayer } = window;
      jwplayer(domId).on(event as keyof EventParams, func);
    },
    resize: (width: number, height: number) => {
      log('pixels', 'resize called');
      const { jwplayer } = window;
      jwplayer(domId).resize(width, height);
    },
    getHeight: (): number => {
      log('pixels', 'getHeight called');
      const { jwplayer } = window;
      return jwplayer(domId).getHeight();
    },
    getAdTag: (): string => {
      log('pixels', 'getAdTag called');
      const { jwplayer } = window;
      if (jwplayer(domId).getConfig().advertising?.schedule?.[0]?.tag) {
        return jwplayer(domId).getConfig().advertising.schedule[0].tag;
      } else if (jwplayer.defaults?.advertising?.schedule?.[0]?.tag) {
        return jwplayer.defaults.advertising.schedule[0].tag;
      }
    },
    setAdTag: (adTag: string) => {
      // only allow tag to be set on first video...
      if (!adTag || currentPlayCount > 0) return;
      enrichAndSetAdTag(domId, adTag);
    },
    getMediaId: (): string => {
      log('pixels', 'getMediaId called');
      const { jwplayer } = window;
      return jwplayer(domId).getConfig().playlistItem.mediaid;
    },
    getPlaylistIndex: (): number => {
      log('pixels', 'getPlaylistIndex called');
      const { jwplayer } = window;
      return jwplayer(domId).getPlaylistIndex();
    },
    getPlaylistItem: (index: number) => {
      log('pixels', 'getPlaylistItem called');
      const { jwplayer } = window;
      return jwplayer(domId).getPlaylistItem(index);
    },
    trigger: (name: string, data: any) => {
      log('pixels', 'trigger called');
      const { jwplayer } = window;
      return jwplayer(domId).trigger(name as keyof EventParams, data);
    },
    getConfig: () => {
      log('pixels', 'getConfig called');
      const { jwplayer } = window;
      return jwplayer(domId).getConfig();
    },
    pause: () => {
      log('pixels', 'pause called');
      const { jwplayer } = window;
      return jwplayer(domId).pause();
    },
    play: () => {
      log('pixels', 'play called');
      const { jwplayer } = window;
      return jwplayer(domId).play();
    },
  };
}
